/* eslint-disable @estee/elc-best-practice/uses-observer */
import * as React from 'react';
import { AnyObject } from '@estee/elc-universal-utils';
import { IOrderConfig } from '@estee/elc-layouts';

export const criteriaFieldsGQLQueryFragment = `
    criteriaFields {
        id
        criteriaName
        isValid
        min
        max
        nextAllowedDate {
            date
            type
        }
        isCurrencyNormalized
    }
`;

export const criteriaAllowedTiersGQLQueryFragment = `
    criteriaAllowedTiers {
        tierNumber
        tierName
    }
`;

export const criteriaRequiredSkusGQLQueryFragment = `
    criteriaRequiredSkus {
        skuId
        name
        link
    }
`;

export type CriteriaFields = {
    id?: string;
    criteriaName: string;
    isValid: boolean;
    min?: number;
    max?: number;
    nextAllowedDate?: {
        date: string;
        type: string;
    };
    inventoryStatus?: string;
    isCurrencyNormalized?: boolean;
};

export type CriteriaAllowedTier = {
    tierNumber: number;
    tierName: string;
};

export type CriteriaRequiredSku = {
    skuId: string;
    name: string;
    link: string;
};

export type OfferNotificationFields = {
    criteriaFieldsList: CriteriaFields[];
    criteriaAllowedTierList: CriteriaAllowedTier[];
    criteriaRequiredSkuList: CriteriaRequiredSku[];
    inventoryStatus?: string;
    pointsNeeded: number;
};

export type OfferNotificationsConfig = {
    messageCharLimit: number,
    viewDetailsText: string;
    messages: {
        [criteriaClassName: string]: unknown | string;
    } | IOrderConfig;
    priorityOrder: string[];
};

interface CriteriaFieldsFlat {
    criteriaName: string;
    date?: string;
    dateType?: string;
    requiredSkuNames?: string;
    min?: number;
    max?: number;
    pointsNeeded?: number;
    allowedTierNumbers?: string;
    allowedTierNames?: string;
    inventoryStatus?: string;
}

interface OfferNotificationContextProps {
    getOfferNotification(offerNotificationFields: OfferNotificationFields): string | null;
    getTruncatedOfferNotificationInfo(offerNotification: string | null | undefined): {truncatedOfferNotification: string | null | undefined, isTruncated: boolean};
    viewDetailsText: string;
}

interface OfferNotificationProviderProps {
    children: React.ReactNode;
    offerNotificationsConfig: OfferNotificationsConfig;
}

const INVALID_INVENTORY_STATUSES = ['TEMP_OUT_OF_STOCK', 'SOLD_OUT', 'COMING_SOON', 'INACTIVE'];

const OfferNotificationContext = React.createContext<OfferNotificationContextProps | undefined>(undefined);

const DEFAULT_CRITERIA_PRIORITY_ORDER: string[] = [
    'InventoryStatus',
    'AvailableLoyaltyPoints',
    'TotalProductCost',
    'TotalPurchaseItems',
    'OneUsePerLoyaltyAnniversary',
    'OneTimeUse',
    'LoyaltyMember',
    'OneMonthEveryYear',
    'Since',
    'Sku'
];

const NESTED_OFFER_NOTIFICATION_MESSAGES_KEY_PATH: { [criteriaClassName: string]: (keyof CriteriaFieldsFlat)[] } = {
    InventoryStatus: ['inventoryStatus'],
    OneMonthEveryYear: ['dateType'],
    Since: ['dateType']
};

const domParser = new DOMParser();

function _getOfferNotificationStringAtPath(offerNotificationObj: AnyObject, path: string): string | null {
    const keys = path.split('.');
    let current = offerNotificationObj;
    for (const key of keys) {
      if (current && key in current) {
        current = current[key];
      } else {
        return null;
      }
    }

    return typeof current === 'string' ? current : null;
}

function _getHighestPriorityCriteria(criteriaFieldsFlatList: CriteriaFieldsFlat[], criteriasPriorityOrder: string[] = DEFAULT_CRITERIA_PRIORITY_ORDER): CriteriaFieldsFlat | null {
    for (const criteria of criteriasPriorityOrder) {
        const foundCriteria = criteriaFieldsFlatList.find(c => c.criteriaName === criteria);
        if (foundCriteria) {
            return foundCriteria;
        }
    }

    return null;
}

function _getFormattedMessage(message: string, criteriaFieldsFlat: CriteriaFieldsFlat): string {
    return message.replace(/::(.*?)::/g, (_, key) => {
        const value = criteriaFieldsFlat[key as keyof CriteriaFieldsFlat];

        return value !== undefined ? String(value) : `::${key}::`;
    });
}

function _addSyntheticInvetoryStatusCriteria(inventoryStatus: string, criteriaFieldsList: CriteriaFields[]) {
    if (INVALID_INVENTORY_STATUSES.includes(inventoryStatus)) {
        criteriaFieldsList.push({
            criteriaName: 'InventoryStatus',
            isValid: false,
            inventoryStatus
        });
    }
}

function _getFilteredCriteriaFieldsFlattened(offerNotificationFields: OfferNotificationFields) {
    const {
        criteriaAllowedTierList,
        criteriaRequiredSkuList,
        inventoryStatus,
        pointsNeeded
    } = offerNotificationFields;
    const allowedTierNumbers = criteriaAllowedTierList ? criteriaAllowedTierList.map(t => t.tierNumber).join() : undefined;
    const allowedTierNames = criteriaAllowedTierList ? criteriaAllowedTierList.map(t => t.tierName).join() : undefined;
    const requiredSkuNames = criteriaRequiredSkuList ? criteriaRequiredSkuList.map(s => `<a href="${s.link}" target="_blank">${s.name}</a>`).join(', ') : undefined;

    _addSyntheticInvetoryStatusCriteria(inventoryStatus || '', offerNotificationFields.criteriaFieldsList);

    return offerNotificationFields.criteriaFieldsList.filter(c => !c.isValid)
        .map(c => ({
                criteriaName: c.criteriaName,
                date: c.nextAllowedDate?.date,
                dateType: c.nextAllowedDate?.type,
                requiredSkuNames,
                min: c.isCurrencyNormalized && c.min ? c.min / 100 : c.min,
                max: c.isCurrencyNormalized && c.max ? c.max / 100 : c.max,
                pointsNeeded,
                allowedTierNumbers,
                allowedTierNames,
                inventoryStatus: c.inventoryStatus
            })) as CriteriaFieldsFlat[];
}

export const OfferNotificationProvider = ({ children, offerNotificationsConfig}: OfferNotificationProviderProps) => {

    const getOfferNotification = (offerNotificationFields: OfferNotificationFields): string | null => {
        if (offerNotificationsConfig?.messages && offerNotificationFields?.criteriaFieldsList?.length > 0) {
            const filteredCriteriaFieldsFlattened = _getFilteredCriteriaFieldsFlattened(offerNotificationFields);
            const highestPriorityCriteria = _getHighestPriorityCriteria(filteredCriteriaFieldsFlattened, offerNotificationsConfig.priorityOrder);

            if (highestPriorityCriteria) {
                const offerNotification = offerNotificationsConfig.messages[highestPriorityCriteria.criteriaName];
                if (offerNotification) {
                    if (typeof offerNotification === 'string') {
                        return _getFormattedMessage(offerNotification, highestPriorityCriteria);
                    } else {
                        const keyPath = NESTED_OFFER_NOTIFICATION_MESSAGES_KEY_PATH[highestPriorityCriteria.criteriaName];
                        if (keyPath) {
                            const offerNotificationString = _getOfferNotificationStringAtPath(
                                offerNotification,
                                keyPath.map(key => highestPriorityCriteria[key] || '').join('.')
                            );

                            return offerNotificationString ? _getFormattedMessage(offerNotificationString, highestPriorityCriteria) : null;
                        }
                    }
                }
            }
        }

        return null;
    };

    const getTruncatedOfferNotificationInfo = (offerNotification: string | null | undefined) => {
        if (!offerNotification) {
            return { truncatedOfferNotification: offerNotification, isTruncated: false };
        } else {
            const { messageCharLimit = 50 } = offerNotificationsConfig;
            const isHTML = /<\/?[a-z][\s\S]*>/i.test(offerNotification);
            if (isHTML) {
                const messageText = domParser.parseFromString(offerNotification, 'text/html').body.textContent || '';

                return messageText.length > messageCharLimit
                    ? {
                        truncatedOfferNotification: `${messageText.slice(0, messageCharLimit)}...`,
                        isTruncated: true
                    }
                    : {
                        truncatedOfferNotification: offerNotification,
                        isTruncated: false
                    };
            } else {
                return offerNotification.length > messageCharLimit
                ? {
                    truncatedOfferNotification: `${offerNotification.slice(0, messageCharLimit)}...`,
                    isTruncated: true
                }
                : {
                    truncatedOfferNotification: offerNotification,
                    isTruncated: false
                };
            }
        }
    };

    return (
        <OfferNotificationContext.Provider value={{
            getOfferNotification,
            getTruncatedOfferNotificationInfo,
            viewDetailsText: offerNotificationsConfig?.viewDetailsText
        }}>
            {children}
        </OfferNotificationContext.Provider>
    );
};

export default OfferNotificationContext;